import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { CalendarCheck2, ChevronDown, ClockArrowUp, Group, History, Ungroup, User2 } from "lucide-react";
import Button from "components/shared/Button";
import Checkbox from "components/shared/inputs/Checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { useHasBulkActions } from "../../components/Timesheet/hooks/bulk-actions/useHasBulkActions";
import { usePreferences } from "../../../Settings/Configuration/hooks/usePreferences";
import { useFilters } from "../../../../utils/hooks/useFilters";
import { useAccess } from "../../../../utils/hooks/access";
import { cn } from "../../../../lib/utils";

interface SelectionBarProps {
    type: string;
    user: any;
    selectedRows: string[];
    allRowIds: string[];
    payGroupItems: any;
    retroAdjustments: any;
    byPayDateRetros: any;
    setSelectedRows: Dispatch<SetStateAction<string[]>>;
    openRetroAdjustmetModal: () => void;
    openProfileDrawer: (user: any) => void;
    openSummaryDrawer: (user: any) => void;
    openRetroDetailsModal: (value: any) => void;
    openOvertimeDrawer: (value: any) => void;
    isGrouped: boolean;
    onGroupToggle: (grouped: boolean) => void;
    isAllExpanded?: boolean;
    onToggleExpandAll?: () => void;
}

function SelectionBar({
    type,
    user,
    allRowIds,
    payGroupItems,
    retroAdjustments,
    byPayDateRetros,
    selectedRows,
    setSelectedRows,
    openRetroAdjustmetModal,
    openProfileDrawer,
    openSummaryDrawer,
    openRetroDetailsModal,
    openOvertimeDrawer,
    isGrouped,
    onGroupToggle,
    isAllExpanded,
    onToggleExpandAll,
}: SelectionBarProps) {
    const { t } = useTranslation();
    const isMyTimesheet = type === "my-timesheet";
    const hasBulkActions = useHasBulkActions(isMyTimesheet ? "my-timesheet" : "user-timesheet");
    const hideRetroAdjustment = usePreferences("timeSheetGeneral.hideRetroAdjustment");
    const calculateOverMultipleProfiles = usePreferences("timeSheet.calculateOverMultipleProfiles");
    const { hasAccess: canCreateRetro } = useAccess("timeSheet.canViewRetro");
    const { hasAccess: canViewMultipleProfiles } = useAccess(isMyTimesheet
        ? "timeSheet.canViewMyMultipleProfiles"
        : "timeSheet.canViewMultipleProfiles");
    const feature = useMemo(() => {
        if (type === "user-timesheet") return "timeSheetTeam";
        if (type === "my-timesheet") return "myTimesheet";
    }, [type]);
    const timesheetFeature = useMemo(() => {
        if (type === "user-timesheet") return "timeSheetTeam.timeSheet";
        if (type === "my-timesheet") return "myTimesheet";
    }, [type]);
    const isProfileHidden = usePreferences(`${feature}.hideUserProfile`);
    const hideWeeklySummary = usePreferences(`${feature}.hideWeeklySummary`);
    const hideDailySummary = usePreferences(`${timesheetFeature}.hideDailySummary`);
    const [{ mode }] = useFilters();

    const isAllSelected = useMemo(
        () => allRowIds.length > 0 && allRowIds.every(id => selectedRows?.includes(id)),
        [allRowIds, selectedRows]
    );

    const isIndeterminate = useMemo(
        () => !isAllSelected && allRowIds.some(id => selectedRows?.includes(id)),
        [isAllSelected, allRowIds, selectedRows]
    );

    const handleSelectAll = useCallback((checked: boolean) => {
        setSelectedRows(checked ? allRowIds : []);
    }, [allRowIds, setSelectedRows]);

    const lockedPayGroupItems = useMemo(() => {
        return payGroupItems?.filter((payGroup: any) => payGroup.status === "locked");
    }, [payGroupItems]);

    const retros = useMemo(() => {
        return retroAdjustments?.reduce((total: any, item: any) => {
            if (item?.retros?.length > 0) {
                total.push(item.retros);
            }
            return total.flat();
        }, []);
    }, [retroAdjustments]);

    const byPayDateRetro = useMemo(() => {
        return byPayDateRetros?.reduce((total: any, item: any) => {
            if (item?.retros?.length > 0) {
                total.push(item.retros);
            }
            return total.flat();
        }, []);
    }, [byPayDateRetros]);

    return (
        <div className="tw-flex tw-items-center tw-justify-between tw-py-2 tw-pl-4.5 tw-w-full">
            <div className="tw-flex tw-items-center tw-gap-4">
                <div className="tw-flex tw-items-center tw-gap-2">
                    {!isGrouped && (
                        <ChevronDown
                            size={20}
                            className={cn("tw-text-gray-600 tw-cursor-pointer tw-transition-transform", isAllExpanded && "tw-rotate-180")}
                            onClick={onToggleExpandAll}
                        />
                    )}
                    {(!isGrouped && hasBulkActions) && (
                        <>
                            <Checkbox
                                className={cn(!isIndeterminate && "tw-bg-white")}
                                checked={isAllSelected}
                                // indeterminate={isIndeterminate}
                                onCheckedChange={handleSelectAll}
                            />
                        </>
                    )}

                    <Button
                        type="button"
                        variant="ghost"
                        tooltip={t(isGrouped ? "ungroup" : "group")}
                        onClick={() => onGroupToggle(!isGrouped)}
                    >
                        {isGrouped ? <Ungroup size={20} /> : <Group size={20} />}
                    </Button>
                </div>

                {(!isMyTimesheet && !hideRetroAdjustment) && (
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <Button
                            type="button"
                            variant="default"
                            hidden={!canCreateRetro || !lockedPayGroupItems?.length || lockedPayGroupItems?.length === 0}
                            onClick={openRetroAdjustmetModal}
                        >
                            {t("retro-adjustment")}
                        </Button>

                        <Button
                            type="button"
                            variant="secondary"
                            hidden={!retros?.length || retros?.length === 0}
                            onClick={() => openRetroDetailsModal({ data: retroAdjustments, user })}
                        >
                            {t("retro-adjustment-details")}
                        </Button>

                        <Button
                            type="button"
                            variant="outline"
                            hidden={!byPayDateRetro?.length || byPayDateRetro?.length === 0}
                            onClick={() => openRetroDetailsModal({ data: byPayDateRetros, user })}
                        >
                            {t("retro-adjustment-by-pay-date")}
                        </Button>
                    </div>
                )}
            </div>

            <div className="tw-flex tw-items-center tw-mr-2">
                {(calculateOverMultipleProfiles && canViewMultipleProfiles && user?.otherProfileIds?.length > 0) && (
                    <Tooltip>
                        <TooltipTrigger
                            asChild
                            onClick={() => openOvertimeDrawer({ ...user, isMyTimesheet })}
                        >
                            <span className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-text-blue-500 tw-cursor-pointer">
                                <ClockArrowUp size={18} />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{t("user-has-multiple-profiles")}</TooltipContent>
                    </Tooltip>
                )}
                {(!hideRetroAdjustment && retroAdjustments?.length > 0) && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-text-amber-500 tw-cursor-pointer">
                                <History size={18} />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>{t("user-has-retro-on-this-period")}</TooltipContent>
                    </Tooltip>
                )}

                <Button
                    variant="ghost"
                    tooltip={t("profile")}
                    onClick={() => openProfileDrawer(user)}
                    hidden={isMyTimesheet || isProfileHidden}
                >
                    <User2 size={20} />
                </Button>

                <Button
                    variant="ghost"
                    tooltip={t("summary")}
                    hidden={(mode !== "day" && hideWeeklySummary) || (mode === "day" && hideDailySummary)}
                    onClick={() => openSummaryDrawer(user)}
                >
                    <CalendarCheck2 size={20} />
                </Button>
            </div>
        </div>
    );
}

export default SelectionBar;
